
import React, { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import { CalculationUtils, validate, formatter } from '../../utility';
import { RentalAddress } from '../NoticeGenerator/StepOne/RentalAddress';
import { calculateIcon, restartIcon } from '../../assets/images';
import { InputBase, CustomDateInput } from '../Helper/InputBase';
import { NormalizationConstants, AppConstants } from '../../constants';
import GeneralUtils from '../../utility/GeneralUtils';
import appConfig from '../../config/app_config';
import moment from 'moment';

const MAX_PORTION_PERCENTAGE = 0.03;

const RentIncreaseInformation = (props) => {
  const { disabled, minDecisionDate, setIsSubmitted, setValidationData, cpiConfig, validationData } = props;

  const [address, setStreetAddress] = useState({
    streetAddress: '',
    city: '',
    postalCode: '',
    errors: {}
 });

  const [decisionData, setDecisionData] = useState({
    decisionDate: '',
    decisionEligibleAmount: '',
    userAccessDate: moment().toDate(),
    phase1RentIncreaseDate: '',
    priorRentAmount: '',
    appliedPhase1EligibleAmount: '',
    phase1RentAmount: '',
    remainingPhase1EligibleAmount: '',
    errors: {}
  });

  useEffect(() => {
    if (JSON.stringify(validationData) === '{}') return;
    setStreetAddress(validationData)
    setDecisionData(validationData)
  },[validationData])

const addressChangeHandler = e => {
  const { name, value } = e.target;
  if (address?.[name] === undefined) return;
  setStreetAddress({...address, [name]: value, errors: { ...address?.errors, [name]: '' }})
}

const decisionDataChangeHandler = e => {
  const { name, value } = e.target;
  if (decisionData?.[name] === undefined) return;
  setDecisionData({...decisionData, [name]: value, errors: { }})
}

const handleDateChange = (value, event) => {
  const { name } = event;
  setDecisionData({...decisionData, [name]: value, errors: { ...decisionData?.errors, [name]: '' }})
}

const setData = () => {
  const { streetAddress, city, postalCode } = address;
  const { decisionDate, decisionEligibleAmount, userAccessDate, phase1RentIncreaseDate, priorRentAmount, appliedPhase1EligibleAmount, phase1RentAmount, remainingPhase1EligibleAmount } = decisionData;
  
  const calcCPIValue = CalculationUtils.getCpiForYear(cpiConfig, moment(phase1RentIncreaseDate).year());
  const logCalcCPI = calcCPIValue ? `year: ${calcCPIValue.year}, value: ${calcCPIValue.value}` : null;
  const p2ToolUsedFor = AppConstants.VALIDATION_PHASE_ONE;
  const calc_p1EarliestServiceDate = moment(decisionDate).startOf('month').add(4, 'months');
  const calc_p1CPIYear = moment(phase1RentIncreaseDate).year();
  const calc_p1CPIValue = (calcCPIValue?.value / 100).toFixed(3);
  const calc_p1MaxCPIAmountStandardRentIncrease = formatter.toFixedNumber(Number(priorRentAmount) * Number(calc_p1CPIValue), 3);
  const calc_p1MaxRentAmountStandardRentIncrease =  Number(priorRentAmount) + calc_p1MaxCPIAmountStandardRentIncrease;
  const p1MaxEligibleAmount = formatter.toFixedNumber((calc_p1MaxRentAmountStandardRentIncrease * MAX_PORTION_PERCENTAGE), 2);
  const calc_p1MaxEligibleAmount = Math.min(decisionEligibleAmount, p1MaxEligibleAmount);
  const calc_p1EligibleErrorAmount = Number(calc_p1MaxEligibleAmount) >= appliedPhase1EligibleAmount ? 0 : Number(appliedPhase1EligibleAmount) - Number(calc_p1MaxEligibleAmount);
  const calc_p1UnusedEligibleAmount = calc_p1MaxEligibleAmount > appliedPhase1EligibleAmount ? formatter.toFixedNumber(calc_p1MaxEligibleAmount - appliedPhase1EligibleAmount, 2) : 0;//const rentAmountAdditionalPortion = Math.min(decisionEligibleAmount, rentMaxAmountAdditionalPortion);
  //NEW phase1RentAmount > calc_p1MaxRentAmountStandardRentIncrease + calc_p1MaxEligibleAmount ? 0 : formatter.toFixedNumber(Number(calc_p1MaxEligibleAmount) - Number(appliedPhase1EligibleAmount), 2)
  //OLD (calc_p1MaxRentAmountStandardRentIncrease + calc_p1MaxEligibleAmount) - Number(phase1RentAmount) > 0 ? (calc_p1MaxRentAmountStandardRentIncrease + calc_p1MaxEligibleAmount) - Number(phase1RentAmount) : 0 //Number(calc_p1MaxEligibleAmount) <= appliedPhase1EligibleAmount ? 0 : formatter.toFixedNumber(Number(calc_p1MaxEligibleAmount) - Number(appliedPhase1EligibleAmount), 2)
  const calc_p1MaxAdditionalRentIncrease = Number(calc_p1MaxRentAmountStandardRentIncrease) + Number(calc_p1MaxEligibleAmount);
  const calc_p1RemainingEligibleAmount = Number(calc_p1MaxEligibleAmount) > 0 ? formatter.toFixedNumber(Number(decisionEligibleAmount) - (Number(calc_p1MaxEligibleAmount)), 2) : 0;
  const calc_p1IsServiceDateValid = moment(moment(phase1RentIncreaseDate).startOf('day')).isSameOrAfter(moment(moment(calc_p1EarliestServiceDate).startOf('day')));
  const calc_p1IsServiceDateExpired = Math.abs(moment(decisionDate).diff(moment(phase1RentIncreaseDate), 'months', true)).toFixed(2) > 12;
  const calc_p1IsAppliedEligibleAmountValid = Number(appliedPhase1EligibleAmount) <= Number(calc_p1MaxEligibleAmount);
  const calc_p1IsPhase2Available = Number(calc_p1RemainingEligibleAmount) > 0 && !calc_p1IsServiceDateExpired;
  const calc_p1IsRentIncreaseAmountValid = Number(phase1RentAmount) <= Number(calc_p1MaxAdditionalRentIncrease);
  const calc_p1RentIncreaseErrorAmount = Number(phase1RentAmount) > Number(calc_p1MaxAdditionalRentIncrease) ? Number(phase1RentAmount) - Number(calc_p1MaxAdditionalRentIncrease) : 0;
  const calc_p1IsRemainingEligibleAmountValid = Number(remainingPhase1EligibleAmount) === Number(calc_p1RemainingEligibleAmount);
  const addressString = `${streetAddress}, ${city}, BC, Canada, ${postalCode}`;

  const calculatedVars = {
    p2ToolUsedFor,
    calcCPIValue,
    calc_p1CPIYear,
    calc_p1CPIValue,
    calc_p1MaxCPIAmountStandardRentIncrease,
    calc_p1MaxRentAmountStandardRentIncrease,
    calc_p1MaxEligibleAmount,
    calc_p1EligibleErrorAmount,
    calc_p1UnusedEligibleAmount,
    calc_p1MaxAdditionalRentIncrease,
    calc_p1RemainingEligibleAmount,
    calc_p1IsServiceDateValid,
    calc_p1IsAppliedEligibleAmountValid,
    calc_p1IsPhase2Available,
    calc_p1IsRentIncreaseAmountValid,
    calc_p1RentIncreaseErrorAmount,
    calc_p1IsRemainingEligibleAmountValid,
    calc_p1IsServiceDateExpired,
    address: addressString
  }

  setValidationData({...address, ...decisionData, ...calculatedVars});
  
  GeneralUtils.log('----- PRINTING ALL PHASE 1 VARIABLES -----', 'success');
  GeneralUtils.log(`
    address: ${addressString}\n
    decisionDate: ${formatter.toDateDisplay(decisionDate)}\n
    decisionEligibleAmount: ${decisionEligibleAmount}\n
    userAccessDate: ${formatter.toDateDisplay(userAccessDate)}\n
    phase1RentIncreaseDate: ${formatter.toDateDisplay(phase1RentIncreaseDate)}\n
    priorRentAmount: ${priorRentAmount}\n
    appliedPhase1EligibleAmount: ${appliedPhase1EligibleAmount}\n
    phase1RentAmount: ${phase1RentAmount}\n
    remainingPhase1EligibleAmount: ${remainingPhase1EligibleAmount}\n
    p2ToolUsedFor: ${p2ToolUsedFor}\n
    logCalcCPI: ${logCalcCPI}\n
    calc_p1MaxCPIAmountStandardRentIncrease: ${calc_p1MaxCPIAmountStandardRentIncrease}\n
    calc_p1MaxRentAmountStandardRentIncrease: ${calc_p1MaxRentAmountStandardRentIncrease}\n
    calc_p1MaxEligibleAmount: ${calc_p1MaxEligibleAmount}\n
    calc_p1EligibleErrorAmount: ${calc_p1EligibleErrorAmount}\n
    calc_p1UnusedEligibleAmount: ${calc_p1UnusedEligibleAmount}\n
    calc_p1MaxAdditionalRentIncrease: ${calc_p1MaxAdditionalRentIncrease}\n
    calc_p1RemainingEligibleAmount: ${calc_p1RemainingEligibleAmount}\n
    calc_p1IsServiceDateValid: ${calc_p1IsServiceDateValid}\n
    calc_p1IsAppliedEligibleAmountValid: ${calc_p1IsAppliedEligibleAmountValid}\n
    calc_p1IsPhase2Available: ${calc_p1IsPhase2Available}\n
    calc_p1IsRentIncreaseAmountValid: ${calc_p1IsRentIncreaseAmountValid}\n
    calc_p1RentIncreaseErrorAmount: ${calc_p1RentIncreaseErrorAmount}\n
    calc_p1IsRemainingEligibleAmountValid: ${calc_p1IsRemainingEligibleAmountValid}\n
    `
    , 'end');
}

const calculate = () => {
  let isValid = true;
  const { streetAddress, city, postalCode } = address;
  const { decisionDate, decisionEligibleAmount, phase1RentIncreaseDate, priorRentAmount, appliedPhase1EligibleAmount, phase1RentAmount, remainingPhase1EligibleAmount } = decisionData;
  
  const addressValidations = [
    {
      streetAddress,
      validationRules: [{ minLength: 6 },{ isRequired: true, errorMsg: 'Address is required' }],
    },
    {
      city,
      validationRules: [{ minLength: 3 }, { isRequired: true, errorMsg: 'City is required' }],
    },
    {
      postalCode,
      validationRules: [{ postalCode: true }, { isRequired: true, errorMsg: 'Postal Code is required' }],
    },
  ];

  const decisionDataValidations = [
    {
      decisionDate,
      validationRules: [{ isRequired: true, errorMsg: 'Date is required' }]
    },
    {
      decisionEligibleAmount,
      validationRules: [{ minValue: 0 }, { isRequired: true, errorMsg: 'Amount is required' }]
    },
    {
      phase1RentIncreaseDate,
      validationRules: [{ isRequired: true, errorMsg: 'Date is required' }]
    },
    {
      priorRentAmount,
      validationRules: [{ minValue: 0 }, { isRequired: true, errorMsg: 'Amount is required' }]
    },
    {
      appliedPhase1EligibleAmount,
      validationRules: [{ minValue: 0 }, { maxValue: decisionEligibleAmount, errorMsg: `Must be less than or equal to eligible amount${decisionEligibleAmount ? `: ${decisionEligibleAmount}` : ''}` }, { isRequired: true, errorMsg: 'Amount is required' }]
    },
    {
      phase1RentAmount,
      validationRules: [{ minValue: Number(priorRentAmount) + Number(appliedPhase1EligibleAmount), errorMsg: `Enter an amount greater than the prior rent plus the applied eligible amount${Number(priorRentAmount) + Number(appliedPhase1EligibleAmount) ? `: ${Number(priorRentAmount) + Number(appliedPhase1EligibleAmount)}` : ''}` }, { isRequired: true, errorMsg: 'Amount is required' }]
    },
    {
      remainingPhase1EligibleAmount,
      validationRules: [{ minValue: 0, errorMsg: 'Amount is required, if you have no remaining amount enter 0' }, { isRequired: true, errorMsg: 'Amount is required' }]
    }
  ]

  const addressErrors = validate(addressValidations);
  const decisionDataErrors = validate(decisionDataValidations);
  
  if (Object.keys(addressErrors).length) {
    isValid = false;
    setStreetAddress({...address, errors: addressErrors });
  } 
  
  if (Object.keys(decisionDataErrors).length) {
    isValid = false;
    setDecisionData({...decisionData, errors: decisionDataErrors });
  }

  if (isValid) {
    setStreetAddress({...address, errors: {}})
    setDecisionData({...decisionData, errors: {}})
    setData();
    setIsSubmitted(true);
  }
} 

const { streetAddress, city, postalCode } = address;
const { decisionDate, decisionEligibleAmount, userAccessDate, phase1RentIncreaseDate, priorRentAmount, appliedPhase1EligibleAmount, phase1RentAmount, remainingPhase1EligibleAmount } = decisionData;
const renderModifyButton = () => disabled ? <span className="w1-step-one__modify" onClick={() => setIsSubmitted(false)}><img src={restartIcon} alt=""/> Modify</span> : null;
  return (
    <div className="">
      <RentalAddress 
        disabled={disabled}
        streetAddress={streetAddress}
        city={city}
        postalCode={postalCode}
        errors={address?.errors}
        handleChange={addressChangeHandler}
      />
      <div className="decision-questions">
        <div className="decision-questions__input-wrapper">
          <CustomDateInput
            name='decisionDate'
            disabled={disabled}
            value={decisionDate}
            errors={decisionData?.errors?.decisionDate}
            cssClass='decision-questions__decisionDate'
            minDate={appConfig.testMode ? null : moment(minDecisionDate).toDate()}
            maxDate={moment().toDate()}
            label='What is the date on the Residential Tenancy Branch decision?'
            handleChange={handleDateChange}
          />
        </div>
        <div className="decision-questions__input-wrapper">
          <InputBase
            name='decisionEligibleAmount'
            startAdornment='$'
            disabled={disabled}
            errors={decisionData?.errors?.decisionEligibleAmount}
            value={decisionEligibleAmount}
            cssClass='decision-questions__eligible-amount validator__eligible-amount'
            type='number'
            label='What was the total eligible amount in the decision for this rental unit?'
            maxLength={NormalizationConstants.maxDollarLength}
            handleChange={decisionDataChangeHandler}
          />
        </div>
      </div>
      { decisionDate ? 
      <div className="validator__questions">
        <CustomDateInput
          name='userAccessDate'
          disabled={!appConfig.testMode || disabled}
          value={userAccessDate}
          cssClass='validator__userAccessDate'
          label={`Today's date`}
          handleChange={handleDateChange}
        />
        <div className="decision-questions__input-wrapper">
          <CustomDateInput
            name='phase1RentIncreaseDate'
            disabled={disabled}
            value={phase1RentIncreaseDate}
            errors={decisionData?.errors?.phase1RentIncreaseDate}
            cssClass='validator__increase-date'
            minDate={decisionData?.decisionDate}
            maxDate={moment().toDate()}
            openToDate={decisionData?.decisionDate}
            label='What was the effective date of your phase 1 (first) additional rent increase?'
            handleChange={handleDateChange}
          />
        </div>
        <div className="decision-questions__input-wrapper">
          <InputBase
            name='priorRentAmount'
            startAdornment='$'
            disabled={disabled}
            errors={decisionData?.errors?.priorRentAmount}
            value={priorRentAmount}
            cssClass='validator__prior-rent'
            type='number'
            label={`What was the rent amount before the phase 1 rent increase ${phase1RentIncreaseDate ? `(total rent before ${formatter.toDateDisplay(phase1RentIncreaseDate, { fullMonth: true })})` : ''}?`}
            maxLength={NormalizationConstants.maxDollarLength}
            handleChange={decisionDataChangeHandler}
          />
        </div>
        <div className="decision-questions__input-wrapper">
          <InputBase
            name='appliedPhase1EligibleAmount'
            startAdornment='$'
            disabled={disabled}
            errors={decisionData?.errors?.appliedPhase1EligibleAmount}
            value={appliedPhase1EligibleAmount}
            cssClass='validator__applied-eligible-amount'
            type='number'
            label={`What was the total eligible amount included in the phase 1 rent increase ${phase1RentIncreaseDate ? `(on ${formatter.toDateDisplay(phase1RentIncreaseDate, { fullMonth: true })})` : ''}?`}
            maxLength={NormalizationConstants.maxDollarLength}
            handleChange={decisionDataChangeHandler}
          />
        </div>
        <div className="decision-questions__input-wrapper">
          <InputBase
            name='phase1RentAmount'
            startAdornment='$'
            disabled={disabled}
            errors={decisionData?.errors?.phase1RentAmount}
            value={phase1RentAmount}
            cssClass='validator__rent-amount'
            type='number'
            label={`What was the rent after the phase 1 rent increase was applied ${phase1RentIncreaseDate ? `(total rent on ${formatter.toDateDisplay(phase1RentIncreaseDate, { fullMonth: true })})` : ''}?`}
            maxLength={NormalizationConstants.maxDollarLength}
            handleChange={decisionDataChangeHandler}
          />
        </div>
        <div className="decision-questions__input-wrapper">
          <InputBase
            name='remainingPhase1EligibleAmount'
            startAdornment='$'
            disabled={disabled}
            errors={decisionData?.errors?.remainingPhase1EligibleAmount}
            value={remainingPhase1EligibleAmount}
            cssClass='validator__remaining-eligible-amount'
            type='number'
            label={`What was the total eligible amount remaining after the phase 1 rent increase ${phase1RentIncreaseDate ? `(on ${formatter.toDateDisplay(phase1RentIncreaseDate, { fullMonth: true })})` : ''}?`}
            maxLength={NormalizationConstants.maxDollarLength}
            handleChange={decisionDataChangeHandler}
          />
        </div>
      </div> : null }
      <div className="w1-step-one__buttons hide-print">
        <Button variant="success" className="step2-buttons-calculate" disabled={disabled} onClick={() => calculate()}>
          <img className="step2-buttons-icon" src={calculateIcon} alt="" /> Calculate
        </Button>
        { renderModifyButton() }
      </div>
    </div>
  );
}

export default RentIncreaseInformation;